/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 17:45:06
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Button, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import EditModal from './modal';

import { NUMBER_METAFIELDS } from 'src/constants/metafield';
import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/tools/metafield/did-you-know';
import ListMetafield from 'src/components/views/tools/metafield/list-metafields';
import InvalidMetafieldsFound from 'src/components/views/tools/metafield/invalid-metafields-found';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import TitleHelp from 'src/components/views/common/title-help';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';

import * as FilterDataActions from 'actions/filter-data-actions';
import * as metafieldActions from 'src/stores/actions/metafield-actions';

type Props = {
  getMetafields: () => void,
  updateMetafield: () => void,
  deleteMetafield: () => void,
  lastSyncDoneAt: number, // seconds
  lastSyncDuration: number, // seconds
  metafieldOptionValues: Array<Object>,
  metafields: Array<Object>,
  invalidMetafields: Array<Object>,
  emptyMetafields: Array<Object>,
  loading: boolean,
  saving: boolean,
  deleting: boolean,
  success: boolean,
  scheduleSyncIn: number,
  waitSyncAt: number,
  emailSupport: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selectedId: number,
  activeModalEdit: boolean
};

class Metafields extends Component<ComposedProps, State> {
  state = {
    selectedId: -1,
    activeModalEdit: false
  };

  componentDidMount() {
    const { getFilterDataByType, getMetafields } = this.props;

    getFilterDataByType('metafields');
    getMetafields();
  }

  componentWillReceiveProps(nextProps) {
    const { saving, deleting, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      updateShowBanner,
      getSyncProcessStatus
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }

      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      loading,
      deleting,
      success,
      syncDuration,
      showScheduleSync,
      metafields,
      emailSupport,
      invalidMetafields,
      emptyMetafields
    } = this.props;

    // Check having metafield
    const hasMetafield = !_.isEmpty(metafields);

    const appliesToMetafields = metafields.filter(metafield => {
      const search = _.get(metafield, 'appliesTo.search', false);
      const filterOption = _.get(metafield, 'appliesTo.filterOption', false);
      return search || filterOption;
    });

    const isExceededMetafields = appliesToMetafields.length >= NUMBER_METAFIELDS;

    const errorMetafields = [].concat(invalidMetafields, emptyMetafields);

    return (
      <div className="BC-Page BC-Page__Metafield">
        <MainMenu menuId="menu-settings-metafields" />
        <Page title={i18n.translate('AdminHTML.Metafield.Metafield.title')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Tool_Metafield_ManualGuide']} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* Did you know? */}
                <DidYouKnow emailSupport={emailSupport} />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* Invalid metafields found*/}
            {errorMetafields.length > 0 ? (
              <Layout.Section>
                <InvalidMetafieldsFound
                  invalidMetafields={invalidMetafields}
                  emptyMetafields={emptyMetafields}
                />
              </Layout.Section>
            ) : (
              ''
            )}

            {/* Processing update banner */}
            {showScheduleSync ? (
              <Layout.Section>
                <ProcessingUpdateBanner syncDuration={syncDuration} featureName="metafield" />
              </Layout.Section>
            ) : (
              ''
            )}

            {/* List of metafield */}
            <Layout.Section>
              <Card sectioned>
                <div>
                  <ListMetafield
                    key={'list-metafields'}
                    loading={loading}
                    list={metafields}
                    error={errorMetafields}
                    deleteMetafield={this.deleteMetafield}
                    deleting={deleting}
                    success={success}
                    handleActiveModalEdit={this.handleActiveModalEdit}
                  />

                  <div className="addNew">
                    <Button plain onClick={() => this.handleActiveModalEdit()}>
                      {i18n.translate('AdminHTML.Metafield.Metafield.addNew')}
                    </Button>
                  </div>
                </div>
              </Card>

              {hasMetafield && isExceededMetafields && (
                <div className="BC-Page BC-Page__Bottom-Text">
                  <TextStyle>
                    {i18n.translate('AdminHTML.Metafield.Metafield.reachedLimit', {
                      nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
                    })}
                  </TextStyle>
                </div>
              )}
            </Layout.Section>
          </Layout>

          {/* Create/Edit modal */}
          <EditModal
            active={this.state.activeModalEdit}
            handleChange={this.handleActiveModalEdit}
            metafield={metafields[this.state.selectedId] || {}}
            isExceededMetafields={isExceededMetafields}
          />
        </Page>
      </div>
    );
  }

  deleteMetafield = metafield => {
    const { deleteMetafield } = this.props;
    deleteMetafield(metafield);
  };

  handleActiveModalEdit = index => {
    const { activeModalEdit } = this.state;

    if (activeModalEdit) {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: -1 });
    } else {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: index });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterDataByType: type =>
    dispatch(FilterDataActions.getFilterDataByType(type, {}, ownProps.i18n)),
  getMetafields: () => dispatch(metafieldActions.getMetafields(ownProps.i18n)),
  deleteMetafield: metafield => dispatch(metafieldActions.deleteMetafield(metafield, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    lastSyncDoneAt: _.get(tenantApp, 'syncControl.lastSyncDoneAt', 0), // seconds
    lastSyncDuration: _.get(tenantApp, 'syncControl.lastSyncDuration', 0), // seconds
    metafieldOptionValues: _.get(state.filterData, 'filterDataByType', []),
    metafields: _.get(state.metafield, 'metafields', []),
    invalidMetafields: _.get(state.metafield, 'invalidMetafields', []),
    emptyMetafields: _.get(state.metafield, 'emptyMetafields', []),
    loading: _.get(state.metafield, 'loading', false),
    saving: _.get(state.metafield, 'saving', false),
    deleting: _.get(state.metafield, 'deleting', false),
    success: _.get(state.metafield, 'success', false),
    scheduleSyncIn: _.get(state.metafield, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.metafield, 'waitSyncAt', 0),
    emailSupport: _.get(state.oauth, 'emailSupport')
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(Metafields)),
  'SyncStoreData'
);
